/**
 * classifyStepError.ts
 *
 * Maps a run step's errorCode/errorMessage to a plain-English summary with a
 * suggested fix for common failure patterns. Returns null when no pattern
 * matches, so callers can fall back to showing the raw error unchanged.
 *
 * Matching is on substrings of (errorCode + " " + errorMessage), lowercased, so
 * it works regardless of the exact backend error codes.
 */

interface Rule {
  test: (haystack: string) => boolean
  summary: string
}

const contains = (...needles: string[]) => (h: string) => needles.some((n) => h.includes(n))

// Order matters: the first matching rule wins.
const RULES: Rule[] = [
  {
    test: contains('auth wall', 'session cookie', 'blocked'),
    summary:
      'LinkedIn blocked this request. Your session cookies may have expired. Go to the Connections tab to refresh them.',
  },
  {
    test: contains('rate limit', '429'),
    summary:
      'Too many requests sent too quickly. The platform will automatically retry at a slower rate.',
  },
  {
    test: contains('timeout', 'deadline exceeded'),
    summary:
      'This step took too long to complete. Try again — if it keeps happening, the target website may be slow or unavailable.',
  },
  {
    test: contains('capability', 'not granted'),
    summary:
      "This skill doesn't have permission to perform this action. Check the skill's capability settings.",
  },
  {
    test: contains('network', 'connection refused'),
    summary:
      'Could not reach the target service. Check that the URL is correct and the service is available.',
  },
]

/**
 * Returns a friendly summary for a known error pattern, or null when there is
 * no mapping (the caller should then show the original error unchanged).
 */
export function classifyStepError(errorCode?: string, errorMessage?: string): string | null {
  const haystack = `${errorCode ?? ''} ${errorMessage ?? ''}`.toLowerCase()
  if (haystack.trim() === '') return null
  for (const rule of RULES) {
    if (rule.test(haystack)) return rule.summary
  }
  return null
}
